import { ChainId, JSBI, TokenAmount, WAVAX } from '@yetiswap/sdk'
import React from 'react'
import { X } from 'react-feather'
import styled from 'styled-components'
import tokenLogo from '../../assets/logo/token-logo.png'
import {
  AIRDROP_TREASURY_ADDRESS,
  DEVFEE_VESTER_ADDRESS,
  LIQ_POOL_MANAGER_ADDRESS,
  MERKLE_AIRDROP_ADDRESS,
  TREASURY_VESTER_ADDRESS,
  YTS
} from '../../constants'
import { useTotalSupply } from '../../data/TotalSupply'
import { useActiveWeb3React } from '../../hooks'
import {
  STAKING_REWARDS_INFO,
  useOldTotalYtsEarned,
  useTotalYtsEarned,
  useTurboTotalYtsEarned
} from '../../state/stake/hooks'
import { useAggregateYtsBalance, useTokenBalance } from '../../state/wallet/hooks'
import { TYPE, YtsTokenAnimated } from '../../theme'
import { AutoColumn } from '../Column'
import { RowBetween } from '../Row'
import { Break, CardBGImage, CardNoise, CardSection, DataCard } from '../earn/styled'
import { usePair } from '../../data/Reserves'

const ContentWrapper = styled(AutoColumn)`
  width: 100%;
`

const ModalUpper = styled(DataCard)`
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  background: radial-gradient(76.02% 75.41% at 1.84% 0%, #98c1d9 0%, #3d5a80 100%);
  padding: 0.5rem;
`

const StyledClose = styled(X)`
  position: absolute;
  right: 16px;
  top: 16px;

  :hover {
    cursor: pointer;
  }
`

/**
 * Content for balance stats modal
 */
export default function YtsBalanceContent({ setShowYtsBalanceModal }: { setShowYtsBalanceModal: any }) {
  const { account, chainId } = useActiveWeb3React()
  const yts = chainId ? YTS[chainId] : undefined

  const total = useAggregateYtsBalance()
  let ytsBalance: TokenAmount | undefined = useTokenBalance(account ?? undefined, yts)
  let ytsToClaim: TokenAmount | undefined = useTotalYtsEarned()

  const ytsTurboToClaim: TokenAmount | undefined = useTurboTotalYtsEarned()
  const ytsOldToClaim: TokenAmount | undefined = useOldTotalYtsEarned()

  if (ytsTurboToClaim) {
    ytsToClaim = ytsToClaim?.add(ytsTurboToClaim)
    ytsBalance = ytsBalance?.add(ytsTurboToClaim)
  }

  if (ytsOldToClaim) {
    ytsToClaim = ytsToClaim?.add(ytsOldToClaim)
    ytsBalance = ytsBalance?.add(ytsOldToClaim)
  }

  const totalSupply: TokenAmount | undefined = useTotalSupply(yts)

  // Determine YTS price in AVAX
  const wavax = WAVAX[chainId ? chainId : 43114]
  const [, avaxYtsTokenPair] = usePair(wavax, yts)
  const oneToken = JSBI.BigInt(1000000000000000000)
  let ytsPrice: number | undefined
  if (avaxYtsTokenPair && yts) {
    const avaxYtsRatio = JSBI.divide(
      JSBI.multiply(oneToken, avaxYtsTokenPair.reserveOf(wavax).raw),
      avaxYtsTokenPair.reserveOf(yts).raw
    )
    ytsPrice = JSBI.toNumber(avaxYtsRatio) / 1000000000000000000
  }
  let circulation: TokenAmount | undefined = undefined

  if (yts) {
    const devFeeAdress = chainId ? DEVFEE_VESTER_ADDRESS[chainId] : undefined
    const airdropTreasuryAdress = chainId ? AIRDROP_TREASURY_ADDRESS[chainId] : undefined
    const treasuryAdress = chainId ? TREASURY_VESTER_ADDRESS[chainId] : undefined
    const merkleAirdropAdress = chainId ? MERKLE_AIRDROP_ADDRESS[chainId] : undefined
    const liqPoolManagerAddress = chainId ? LIQ_POOL_MANAGER_ADDRESS[chainId] : undefined

    const zeroCurrencyAmount = new TokenAmount(
      yts,
      JSBI.multiply(JSBI.BigInt(0), JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(18)))
    )
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const balanceDevFee = useTokenBalance(devFeeAdress ?? undefined, yts) ?? zeroCurrencyAmount
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const balanceAirdropTreasurye = useTokenBalance(airdropTreasuryAdress ?? undefined, yts) ?? zeroCurrencyAmount
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const balanceTreasuryAdress = useTokenBalance(treasuryAdress ?? undefined, yts) ?? zeroCurrencyAmount
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const balanceMerkleAirdrop = useTokenBalance(merkleAirdropAdress ?? undefined, yts) ?? zeroCurrencyAmount
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const balanceLiqPoolManagerAddress = useTokenBalance(liqPoolManagerAddress ?? undefined, yts) ?? zeroCurrencyAmount

    const stakingPools: TokenAmount[] | undefined = STAKING_REWARDS_INFO[chainId ? chainId : ChainId.FUJI]?.map(
      // eslint-disable-next-line react-hooks/rules-of-hooks
      item => useTokenBalance(item.stakingRewardAddress, yts) ?? zeroCurrencyAmount
    )
    let summ = balanceDevFee
      .add(balanceAirdropTreasurye)
      .add(balanceTreasuryAdress)
      .add(balanceMerkleAirdrop)
      .add(balanceLiqPoolManagerAddress)

    stakingPools?.forEach((item: TokenAmount) => {
      summ = summ.add(item)
    })

    const wholeAmount = JSBI.BigInt(556_000_000)
    const amm = new TokenAmount(yts, JSBI.multiply(wholeAmount, JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(18))))

    circulation = amm.subtract(summ)
  }

  return (
    <ContentWrapper gap="lg">
      <ModalUpper>
        <CardBGImage />
        <CardNoise />
        <CardSection gap="md">
          <RowBetween>
            <TYPE.white color="white">Your YTS Breakdown</TYPE.white>
            <StyledClose stroke="white" onClick={() => setShowYtsBalanceModal(false)} />
          </RowBetween>
        </CardSection>
        <Break />
        {account && (
          <>
            <CardSection gap="sm">
              <AutoColumn gap="md" justify="center">
                <YtsTokenAnimated width="80px" src={tokenLogo} />{' '}
                <TYPE.white fontSize={48} fontWeight={600} color="white">
                  {total?.toFixed(2, { groupSeparator: ',' })}
                </TYPE.white>
              </AutoColumn>
              <AutoColumn gap="md">
                <RowBetween>
                  <TYPE.white color="white">Balance:</TYPE.white>
                  <TYPE.white color="white">{ytsBalance?.toFixed(2, { groupSeparator: ',' })}</TYPE.white>
                </RowBetween>
                <RowBetween>
                  <TYPE.white color="white">Unclaimed:</TYPE.white>
                  <TYPE.white color="white">
                    {ytsToClaim?.toFixed(4, { groupSeparator: ',' })}{' '}
                    {/*ytsToClaim && ytsToClaim.greaterThan('0') && (
                      <StyledInternalLink onClick={() => setShowYtsBalanceModal(false)} to="/yts">
                        (claim)
                      </StyledInternalLink>
                    )*/}
                  </TYPE.white>
                </RowBetween>
              </AutoColumn>
            </CardSection>
            <Break />
          </>
        )}
        <CardSection gap="sm">
          <AutoColumn gap="md">
            <RowBetween>
              <TYPE.white color="white">YTS price:</TYPE.white>
              <TYPE.white color="white">{ytsPrice?.toFixed(5) ?? '-'} AVAX</TYPE.white>
            </RowBetween>
            <RowBetween>
              <TYPE.white color="white">YTS in circulation:</TYPE.white>
              <TYPE.white color="white">{circulation?.toFixed(0, { groupSeparator: ',' })}</TYPE.white>
            </RowBetween>
            <RowBetween>
              <TYPE.white color="white">Total Supply</TYPE.white>
              <TYPE.white color="white">{totalSupply?.toFixed(0, { groupSeparator: ',' })}</TYPE.white>
            </RowBetween>
          </AutoColumn>
        </CardSection>
      </ModalUpper>
    </ContentWrapper>
  )
}
